import { TGuestPreChatForm } from '~/features/preChatForms/models'
import {
  ECustomInstructionsKeys,
  EEmailStatus,
  ELoopingAudio,
  EOrchestratorType,
  EOutboundPhoneStatus,
  EPhoneStatus,
  EPreConnectMessageSound,
  ERecordingStartTrigger,
  ERecordingStopTrigger,
  ETextStatus,
  TChatbotSettingsLayout,
  TCustomInstruction,
  TEscalation,
  TLink,
} from '~/features/settings/models'
import { TUser } from '~/features/user/models'
import { TTtsAdditionalConfig } from '~/features/voiceTemplates/models'
import {
  EAgentNotifyWhen,
  ECTAFriendly,
  ELocationTypeValues,
  ENotificationType,
  EPersonalityFriendlyName,
  EPersonalityValues,
  EScheduleValues,
  ETextMethodValues,
} from '~/models/features'

export type TLocationAgent = {
  id: string
  agentReferenceName: string | null
  agentStatus: EAgentStatus | null
}

export type TLocationPhoneNumber = {
  id: string
  phoneNumber: string
  handlingMode: string
}

export enum ELocationAbcClubIdKeys {
  key = 'key',
  provider = 'provider',
  value = 'value',
}

export type TLocationAbcClubId = {
  [ELocationAbcClubIdKeys.key]: string
  [ELocationAbcClubIdKeys.provider]: string
  [ELocationAbcClubIdKeys.value]: string
}

export enum ELocationKeys {
  id = 'id',
  name = 'name',
  type = 'type',
  orgId = 'orgId',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  streetAddress1 = 'streetAddress1',
  streetAddress2 = 'streetAddress2',
  city = 'city',
  state = 'state',
  zipCode = 'zipCode',
  country = 'country',
  hoursText = 'hoursText',
  timezone = 'timezone',
  website = 'website',
  status = 'status',
  abcClubIds = 'abcClubIds',
  agents = 'agents',
  phoneNumbers = 'phoneNumbers',
}

export enum ELocationStatus {
  active = 'active',
  draft = 'draft',
}

export enum ELocationType {
  single = 'single',
  multi = 'multi',
  virtual = 'virtual',
}

export type TLocation = {
  [ELocationKeys.id]: string
  [ELocationKeys.name]: string
  [ELocationKeys.type]: ELocationType | string
  [ELocationKeys.orgId]: string
  [ELocationKeys.createdDate]: string
  [ELocationKeys.updatedDate]: string
  [ELocationKeys.streetAddress1]: string | null
  [ELocationKeys.streetAddress2]: string | null
  [ELocationKeys.city]: string | null
  [ELocationKeys.state]: string | null
  [ELocationKeys.zipCode]: string | null
  [ELocationKeys.country]: string | null
  [ELocationKeys.hoursText]: string | null
  [ELocationKeys.timezone]: string | null
  [ELocationKeys.website]: string | null
  [ELocationKeys.status]?: ELocationStatus | null
  [ELocationKeys.abcClubIds]?: TLocationAbcClubId[]
  [ELocationKeys.agents]: TLocationAgent[]
  [ELocationKeys.phoneNumbers]: TLocationPhoneNumber[]
}

export type TUpdateLocationPayload = {
  [ELocationKeys.id]: string
} & Partial<
  Pick<
    TLocation,
    | ELocationKeys.abcClubIds
    | ELocationKeys.name
    | ELocationKeys.type
    | ELocationKeys.streetAddress1
    | ELocationKeys.streetAddress2
    | ELocationKeys.city
    | ELocationKeys.state
    | ELocationKeys.zipCode
    | ELocationKeys.country
    | ELocationKeys.hoursText
    | ELocationKeys.timezone
    | ELocationKeys.website
    | ELocationKeys.status
  >
>

export type TUpdateLocationsPayload = {
  locations: TUpdateLocationPayload[]
}

export enum EIntegrationMappingKeys {
  provider = 'provider',
  providerKey = 'providerKey',
  locations = 'locations',
}

export enum EIntegrationMappingScopeKeys {
  id = 'id',
  connectionId = 'connectionId',
  connectionStatus = 'connectionStatus',
  activatedAt = 'activatedAt',
}

export enum EIntegrationMappingManagedExternalIdKeys {
  id = 'id',
  value = 'value',
  connectionScope = 'connectionScope',
}

export enum EIntegrationMappingAgentKeys {
  id = 'id',
  displayName = 'displayName',
  status = 'status',
  hasManagedExternalId = 'hasManagedExternalId',
  managedExternalId = 'managedExternalId',
}

export enum EIntegrationMappingLocationAddressKeys {
  streetAddress1 = 'streetAddress1',
  streetAddress2 = 'streetAddress2',
  city = 'city',
  state = 'state',
  zipCode = 'zipCode',
  country = 'country',
}

export enum EIntegrationMappingStatus {
  failed = 'failed',
  inactive = 'inactive',
  pending = 'pending',
  ready = 'ready',
  unmapped = 'unmapped',
}

export enum EIntegrationMappingInitialSyncStatus {
  completed = 'completed',
  failed = 'failed',
  pending = 'pending',
  running = 'running',
}

export enum EIntegrationMappingClubKeys {
  clubNumber = 'clubNumber',
  connectionId = 'connectionId',
  initialSyncCompletedAt = 'initialSyncCompletedAt',
  initialSyncStatus = 'initialSyncStatus',
  webhookActivatedAt = 'webhookActivatedAt',
  webhookStatus = 'webhookStatus',
}

export type TIntegrationMappingClub = {
  [EIntegrationMappingClubKeys.clubNumber]: string
  [EIntegrationMappingClubKeys.connectionId]: string | null
  [EIntegrationMappingClubKeys.initialSyncCompletedAt]: string | null
  [EIntegrationMappingClubKeys.initialSyncStatus]: EIntegrationMappingInitialSyncStatus
  [EIntegrationMappingClubKeys.webhookActivatedAt]: string | null
  [EIntegrationMappingClubKeys.webhookStatus]: string
}

export enum EIntegrationMappingLocationKeys {
  id = 'id',
  name = 'name',
  type = 'type',
  status = 'status',
  address = 'address',
  abcClubIds = 'abcClubIds',
  agents = 'agents',
  clubs = 'clubs',
  integrationStatus = 'integrationStatus',
}

export type TIntegrationMappingScope = {
  [EIntegrationMappingScopeKeys.id]: string
  [EIntegrationMappingScopeKeys.connectionId]: string
  [EIntegrationMappingScopeKeys.connectionStatus]: string | null
  [EIntegrationMappingScopeKeys.activatedAt]: string | null
}

export type TIntegrationMappingManagedExternalId = {
  [EIntegrationMappingManagedExternalIdKeys.id]: string
  [EIntegrationMappingManagedExternalIdKeys.value]: string
  [EIntegrationMappingManagedExternalIdKeys.connectionScope]: TIntegrationMappingScope | null
}

export type TIntegrationMappingAgent = {
  [EIntegrationMappingAgentKeys.id]: string
  [EIntegrationMappingAgentKeys.displayName]: string | null
  [EIntegrationMappingAgentKeys.status]: string | null
  [EIntegrationMappingAgentKeys.hasManagedExternalId]: boolean
  [EIntegrationMappingAgentKeys.managedExternalId]: TIntegrationMappingManagedExternalId | null
}

export type TIntegrationMappingLocationAddress = {
  [EIntegrationMappingLocationAddressKeys.streetAddress1]: string | null
  [EIntegrationMappingLocationAddressKeys.streetAddress2]: string | null
  [EIntegrationMappingLocationAddressKeys.city]: string | null
  [EIntegrationMappingLocationAddressKeys.state]: string | null
  [EIntegrationMappingLocationAddressKeys.zipCode]: string | null
  [EIntegrationMappingLocationAddressKeys.country]: string | null
}

export type TIntegrationMappingLocation = {
  [EIntegrationMappingLocationKeys.id]: string
  [EIntegrationMappingLocationKeys.name]: string
  [EIntegrationMappingLocationKeys.type]: string
  [EIntegrationMappingLocationKeys.status]: string | null
  [EIntegrationMappingLocationKeys.address]: TIntegrationMappingLocationAddress
  [EIntegrationMappingLocationKeys.abcClubIds]: TLocationAbcClubId[]
  [EIntegrationMappingLocationKeys.agents]: TIntegrationMappingAgent[]
  [EIntegrationMappingLocationKeys.clubs]?: TIntegrationMappingClub[]
  [EIntegrationMappingLocationKeys.integrationStatus]?: EIntegrationMappingStatus
}

export type TLocationsIntegrationMapping = {
  [EIntegrationMappingKeys.provider]: string
  [EIntegrationMappingKeys.providerKey]: string
  [EIntegrationMappingKeys.locations]: TIntegrationMappingLocation[]
}

export enum EScheduleKeys {
  id = 'id',
  orgId = 'orgId',
  name = 'name',
  unavailableMessage = 'unavailableMessage',
  isAlwaysOpen = 'isAlwaysOpen',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  ScheduleHours = 'ScheduleHours',
  ScheduleAssignment = 'ScheduleAssignment',
}

export enum EScheduleHoursKeys {
  id = 'id',
  scheduleId = 'scheduleId',
  dayOfWeek = 'dayOfWeek',
  openTime = 'openTime',
  closeTime = 'closeTime',
}

export enum EScheduleAssignmentKeys {
  id = 'id',
  agentId = 'agentId',
  scheduleId = 'scheduleId',
  purposeKey = 'purposeKey',
  orgId = 'orgId',
  scopeType = 'scopeType',
}

export enum EScheduleAssignmentScopeType {
  agent = 'agent',
  location = 'location',
  orgDefault = 'orgDefault',
}

export enum ELiveTransferSchedulePurposeKey {
  liveTransferAvailability = 'live_transfer_availability',
}

export enum EAssignAgentScheduleFormKeys {
  scheduleId = 'scheduleId',
}

export type TScheduleHours = {
  [EScheduleHoursKeys.id]: string
  [EScheduleHoursKeys.scheduleId]: string
  [EScheduleHoursKeys.dayOfWeek]: number
  [EScheduleHoursKeys.openTime]: number
  [EScheduleHoursKeys.closeTime]: number
}

export type TScheduleAssignment = {
  [EScheduleAssignmentKeys.id]: string
  [EScheduleAssignmentKeys.agentId]?: string | null
  [EScheduleAssignmentKeys.scheduleId]: string
  [EScheduleAssignmentKeys.purposeKey]?: string
  [EScheduleAssignmentKeys.orgId]?: string
  [EScheduleAssignmentKeys.scopeType]?: EScheduleAssignmentScopeType | string
}

export type TSchedule = {
  [EScheduleKeys.id]: string
  [EScheduleKeys.orgId]: string
  [EScheduleKeys.name]?: string | null
  [EScheduleKeys.unavailableMessage]?: string | null
  [EScheduleKeys.isAlwaysOpen]?: boolean
  [EScheduleKeys.createdDate]?: string
  [EScheduleKeys.updatedDate]?: string
  [EScheduleKeys.ScheduleHours]?: TScheduleHours[]
  [EScheduleKeys.ScheduleAssignment]?: TScheduleAssignment[]
}

export type TAssignAgentSchedulePayload = {
  agentIds: string[]
  purposeKey?: string
}

export enum EAgentTransferStatusKeys {
  agentId = 'agentId',
  name = 'name',
  transfersDisabled = 'transfersDisabled',
}

export type TAgentTransferStatus = {
  [EAgentTransferStatusKeys.agentId]: string
  [EAgentTransferStatusKeys.name]: string
  [EAgentTransferStatusKeys.transfersDisabled]: boolean
}

export type TSetTransfersDisabledPayload = {
  agentId?: string
  agentIds?: string[]
  orgId?: string
}

export type TUpdateTransfersCountResponse = {
  count: number
}

export type TUpdateTransferPauseStatusesPayload = {
  agentIdsToDisable: string[]
  agentIdsToEnable: string[]
}

export type TCreateScheduleHoursPayload = {
  dayOfWeek: number
  openTime: number
  closeTime: number
}

export type TCreateSchedulePayload = {
  orgId: string
  name?: string
  unavailableMessage?: string
  isAlwaysOpen?: boolean
  setAsOrgDefault?: boolean
  agentIds?: string[]
  purposeKey?: string
  hours?: TCreateScheduleHoursPayload[]
}

export type TUpdateSchedulePayload = {
  scheduleId: string
  name?: string
  unavailableMessage?: string
  isAlwaysOpen?: boolean
  hours?: TCreateScheduleHoursPayload[]
  setAsOrgDefault?: boolean
  addAgentIds?: string[]
  removeAgentIds?: string[]
  purposeKey?: string
}

export type TAssignAgentScheduleForm = {
  [EAssignAgentScheduleFormKeys.scheduleId]: string
}

export type TUpdateLocationsResponse = {
  total: number
  succeeded: number
  failed: number
  results: Array<
    | { id: string; success: true }
    | { id: string; success: false; error: 'not_found_or_unauthorized' }
  >
}

export type TCreateLocationPayload = {
  [ELocationKeys.name]: string
  [ELocationKeys.type]: ELocationType | string
} & Partial<
  Pick<
    TLocation,
    | ELocationKeys.streetAddress1
    | ELocationKeys.streetAddress2
    | ELocationKeys.city
    | ELocationKeys.state
    | ELocationKeys.zipCode
    | ELocationKeys.country
    | ELocationKeys.hoursText
    | ELocationKeys.timezone
    | ELocationKeys.website
    | ELocationKeys.status
  >
> & {
    agentIds?: string[]
    phoneNumberIds?: string[]
  }

export type TCreateLocationsPayload = {
  locations: TCreateLocationPayload[]
}

export type TCreateLocationsResponse = {
  total: number
  created: Array<{
    id: string
    name: string
    type: ELocationType | string
    status: ELocationStatus
    assignedAgentIds: string[]
    assignedPhoneNumberIds: string[]
  }>
  cascaded: {
    agentsAssigned: number
    phoneNumbersAssigned: number
  }
}

export type TDeleteLocationsPayload = {
  locationIds: string[]
  replaceWith?: string
  replaceWithMap?: Record<string, string>
}

export type TDeleteLocationsResponse = {
  total: number
  succeeded: number
  failed: number
  results: Array<
    | { id: string; success: true; reassignedTo?: string }
    | { id: string; success: false; error: string }
  >
  cascaded: {
    agentsUpdated: number
    phoneNumbersUpdated: number
    sipEndpointsUpdated: number
    conversationsUpdated: number
    userAccessRowsDeleted: number
  }
}

export enum EWebScrapeStatus {
  unloaded = 'unloaded',
  started = 'Started',
  loaded = 'Loaded',
  failed = 'Failed',
}

export enum EChatStatus {
  active = 'active',
  paused = 'paused',
  disabled = 'disabled',
  lockedDisabled = 'lockedDisabled',
}

export type TWebScrapeStatus = {
  scrapeStatus: EWebScrapeStatus
}

export type TOrg = {
  id: string
  createdDate: Date
  agents: TAgent[]
  User?: TUser[]
}
export enum EAgentKeys {
  id = 'id',
  agentReferenceName = 'agentReferenceName',
  agentEmail = 'agentEmail',
  emailStatus = 'emailStatus',
  agentName = 'agentName',
  orgId = 'orgId',
  Org = 'Org',
  brandId = 'brandId',
  businessName = 'businessName',
  service = 'service',
  siteLink = 'siteLink',
  locationType = 'locationType',
  location = 'location',
  pricing = 'pricing',
  questions = 'questions',
  facts = 'facts',
  calendlyLink = 'calendlyLink',
  cta = 'cta',
  ctaText = 'ctaText',
  customURL = 'customURL',
  emailConsent = 'emailConsent',
  mobileConsent = 'mobileConsent',
  notifyEmail = 'notifyEmail',
  notifyMobile = 'notifyMobile',
  notifyType = 'notifyType',
  notifyWhen = 'notifyWhen',
  personality = 'personality',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  phoneIntro = 'phoneIntro',
  phoneNumber = 'phoneNumber',
  agentLanguage = 'agentLanguage',
  logoImgUrl = 'logoImgUrl',
  scheduleType = 'scheduleType',
  zapierId = 'zapierId',
  zapierCustomerResponsesOnly = 'zapierCustomerResponsesOnly',
  zapierParameters = 'zapierParameters',
  customLinks = 'customLinks',
  chatIntro = 'chatIntro',
  chatbotImgURL = 'chatbotImgURL',
  showLibby = 'showLibby',
  showBranding = 'showBranding',
  chatFriendlyUrl = 'chatFriendlyUrl',
  phoneStatus = 'phoneStatus',
  phoneVoice = 'phoneVoice',
  phoneIntroRecording = 'phoneIntroRecording',
  textMethod = 'textMethod',
  scrapeStatus = 'scrapeStatus',
  customInstructions = 'customInstructions',
  escalations = 'escalations',
  chatStatus = 'chatStatus',
  textStatus = 'textStatus',
  loopingAudio = 'loopingAudio',
  agentEmailCustom = 'agentEmailCustom',
  outboundPhoneStatus = 'outboundPhoneStatus',
  agentTimezone = 'agentTimezone',
  defaultCountryCode = 'defaultCountryCode',
  customAgentRules = 'customAgentRules',
  chatSettings = 'chatSettings',
  emailSettings = 'emailSettings',
  textSettings = 'textSettings',
  phoneSettings = 'phoneSettings',
  agentStatus = 'agentStatus',
  externalIdsCount = 'externalIdsCount',
  liveTransferAvailabilityScheduleId = 'liveTransferAvailabilityScheduleId',
  locationId = 'locationId',
}

export enum EAgentStatus {
  active = 'active',
  disabled = 'disabled',
  lockedDisabled = 'lockedDisabled',
}

export type TAgentFact =
  | string
  | {
      id?: string
      text: string
    }

export enum EIconShape {
  square = 'square',
  circle = 'circle',
}

export enum EPopupDevices {
  desktop = 'desktop',
  mobile = 'mobile',
}

export type TChatSettings = {
  formId?: string | null
  form?: TGuestPreChatForm | null
  layout?: TChatbotSettingsLayout
  legacyChatbotId?: string
  chatRules?: string[]
  chatTitle?: string
  chatSubtitle?: string
  embedPopupAutoCloseDelaySeconds?: number | null
  embedPopupAutoCloseEnabled: boolean
  embedPopupCustomImageUrl?: string | null
  embedPopupDelaySeconds?: number | null
  embedPopupDevices?: null | EPopupDevices[]
  embedPopupEnabled: boolean
  embedPopupMessage?: string | null
  embedIconBGColor: string | null
  embedIconColor: string | null
  embedIconShape?: EIconShape | null
}

export type TEmailSettings = {
  isDefaultSignature: boolean
  signature: string
  emailRules?: string[]
}

export type TTextSettings = {
  textRules?: string[]
}

export type TZapierParameters = Record<string, string>

export type TAgent = {
  [EAgentKeys.id]: string
  [EAgentKeys.agentEmail]?: string | null
  [EAgentKeys.agentReferenceName]?: string | null
  [EAgentKeys.emailStatus]?: EEmailStatus
  [EAgentKeys.agentName]?: string
  [EAgentKeys.orgId]?: string
  [EAgentKeys.Org]?: TOrg
  [EAgentKeys.brandId]?: string | null
  [EAgentKeys.businessName]: string
  [EAgentKeys.service]: string
  [EAgentKeys.siteLink]?: string
  [EAgentKeys.locationType]?: ELocationTypeValues
  [EAgentKeys.location]?: string
  [EAgentKeys.pricing]?: string
  [EAgentKeys.questions]?: string[]
  [EAgentKeys.facts]?: TAgentFact[]
  [EAgentKeys.calendlyLink]?: string
  [EAgentKeys.cta]?: ECTAFriendly
  [EAgentKeys.ctaText]?: string | null
  [EAgentKeys.customURL]?: TLink[] | string
  [EAgentKeys.emailConsent]?: string
  [EAgentKeys.mobileConsent]?: boolean
  [EAgentKeys.notifyEmail]?: string
  [EAgentKeys.notifyMobile]?: string
  [EAgentKeys.notifyType]?: ENotificationType | null
  [EAgentKeys.notifyWhen]?: EAgentNotifyWhen
  [EAgentKeys.personality]?: EPersonalityValues
  [EAgentKeys.createdDate]?: Date
  [EAgentKeys.updatedDate]?: Date
  [EAgentKeys.phoneIntro]?: string
  [EAgentKeys.phoneNumber]?: string
  [EAgentKeys.agentLanguage]?: string
  [EAgentKeys.logoImgUrl]?: string | null
  [EAgentKeys.scheduleType]?: EScheduleValues
  [EAgentKeys.zapierId]?: string | null
  [EAgentKeys.zapierCustomerResponsesOnly]?: boolean
  [EAgentKeys.zapierParameters]?: TZapierParameters
  [EAgentKeys.customLinks]?: TLink[]
  [EAgentKeys.chatIntro]?: string
  [EAgentKeys.chatbotImgURL]?: string
  [EAgentKeys.showLibby]?: boolean
  [EAgentKeys.showBranding]?: boolean
  [EAgentKeys.chatFriendlyUrl]?: string | null
  [EAgentKeys.phoneStatus]?: EPhoneStatus
  [EAgentKeys.phoneVoice]?: string
  [EAgentKeys.phoneIntroRecording]?: string
  [EAgentKeys.textMethod]?: ETextMethodValues
  [EAgentKeys.scrapeStatus]?: EWebScrapeStatus
  [EAgentKeys.customInstructions]?: Array<object | TCustomInstruction> | null
  [EAgentKeys.escalations]?: TEscalation[] | []
  [EAgentKeys.chatStatus]?: EChatStatus
  [EAgentKeys.textStatus]?: ETextStatus
  [EAgentKeys.loopingAudio]?: ELoopingAudio
  [EAgentKeys.agentEmailCustom]?: string | null
  [EAgentKeys.outboundPhoneStatus]?: EOutboundPhoneStatus
  [EAgentKeys.agentTimezone]?: string
  [EAgentKeys.defaultCountryCode]?: string
  [EAgentKeys.customAgentRules]?: string[] | null
  [EAgentKeys.chatSettings]?: TChatSettings | Array<TChatSettings>
  [EAgentKeys.emailSettings]?: TEmailSettings
  [EAgentKeys.textSettings]?: TTextSettings
  [EAgentKeys.phoneSettings]?: TAgentPhoneSettings
  [EAgentKeys.agentStatus]?: EAgentStatus
  [EAgentKeys.externalIdsCount]?: number
  [EAgentKeys.liveTransferAvailabilityScheduleId]?: string | null
  [EAgentKeys.locationId]?: string | null
}

export type TUpdateAgent = Partial<{
  agent: Partial<
    Omit<
      TAgent,
      'id' | 'chatSettings' | 'emailSettings' | 'phoneSettings' | 'textSettings'
    >
  >
  chatSettings: Partial<TChatSettings>
  emailSettings: Partial<TEmailSettings>
  phoneSettings: Partial<TAgentPhoneSettings>
  textSettings: Partial<TTextSettings>
}>

export enum EImageType {
  logoImg = 'logoImg',
  chatbotImg = 'chatbotImg',
  embedPopupCustomImageUrl = 'embedPopupCustomImageUrl',
}

export type TCreateAgentPayload = Omit<TAgent, 'id'> & { partnerId?: string }

/**
 * Trimmed shape returned by agent-service's public/guest agent endpoints —
 * exactly the fields `usePopulateChatbot` needs to render the chat widget for
 * anonymous visitors (`/guest/agents/:agentId`, by-friendly-url, and
 * by-legacy-id). `TAgent` structurally satisfies this too.
 */
export type TChatbotAgent = {
  [EAgentKeys.id]: string
  [EAgentKeys.businessName]: string
  [EAgentKeys.agentName]?: string
  [EAgentKeys.chatIntro]?: string
  [EAgentKeys.chatbotImgURL]?: string
  [EAgentKeys.service]: string
  [EAgentKeys.customLinks]?: TLink[]
  [EAgentKeys.showLibby]?: boolean
  [EAgentKeys.showBranding]?: boolean
  [EAgentKeys.calendlyLink]?: string
  [EAgentKeys.chatStatus]?: EChatStatus
  [EAgentKeys.agentStatus]?: EAgentStatus
  [EAgentKeys.chatSettings]?: TChatSettings | Array<TChatSettings>
}

export type TAgentChatbot = {
  id: string
  createdDate: Date
  updatedDate: Date
  agentId: string
  layout: TChatbotSettingsLayout
  legacyChatbotId: string
}

export type TCreateChatbotSettingsPayload = {
  agentId: string
  layout: TChatbotSettingsLayout
}

export type TCachedAgent = {
  data: TAgent
}

export const personalityMapping = {
  [EPersonalityFriendlyName.professional]: EPersonalityValues.professional,
  [EPersonalityFriendlyName.jokey]: EPersonalityValues.jokey,
  [EPersonalityFriendlyName.enthusiastic]: EPersonalityValues.enthusiastic,
  [EPersonalityFriendlyName.laidBack]: EPersonalityValues.laidBack,
}

export type TAssistantTableData = Pick<
  TAgent,
  | EAgentKeys.id
  | EAgentKeys.agentReferenceName
  | EAgentKeys.phoneNumber
  | EAgentKeys.agentEmail
  | EAgentKeys.agentEmailCustom
  | EAgentKeys.phoneStatus
  | EAgentKeys.emailStatus
  | EAgentKeys.chatStatus
  | EAgentKeys.chatFriendlyUrl
  | EAgentKeys.agentStatus
  | EAgentKeys.businessName
  | EAgentKeys.externalIdsCount
>

export type TCreateAssistant = {
  [EAgentKeys.agentReferenceName]: string
  [EAgentKeys.service]: string
  [EAgentKeys.businessName]?: string
  [EAgentKeys.locationId]: string
}

export type TBulkAssignAgentEntry = {
  agentId: string
  locationId?: string | null
  phoneNumberIds?: string[]
  phoneNumberIdsToRemove?: string[]
}

export type TBulkAssignAgentsPayload = {
  assignments: TBulkAssignAgentEntry[]
}

export type TBulkAssignAgentsResponse = {
  total: number
  results: Array<{
    agentId: string
    locationSet: boolean
    locationCleared: boolean
    phoneNumbersAssigned: number
    phoneNumbersUnassigned: number
  }>
  totals: {
    agentsTouched: number
    phoneNumbersAssigned: number
    phoneNumbersUnassigned: number
  }
}

export type TAgentPhoneSettings = {
  id: string
  agentId: string
  keywords: string[]
  outageResponse: string
  patience: number
  noiseTolerance: number
  createdDate: Date
  updatedDate: Date
  phoneRules: string[]
  backupNumber: string
  textFollowUpClosing: string
  textFollowUpTemplate?: string
  phoneOrchestrationType: EOrchestratorType
  recordingEnabled: boolean
  recordingStartTrigger: ERecordingStartTrigger
  recordingStopTrigger: ERecordingStopTrigger
  allowIntroInterrupt: boolean
  preConnectMessageEnabled?: boolean | null
  preConnectMessageText?: string
  preConnectMessageVoiceId?: string | null
  preConnectMessageSound?: EPreConnectMessageSound | null
  preConnectMessageRecordingUrl?: string | null
  llmModel?: string
  ttsProvider?: string
  ttsSpeed?: number
  ttsAdditionalConfig?: TTtsAdditionalConfig | null
  promptOverride?: string | null
}

export type TAgentPhonePrompt = {
  agentId: string
  prompt: string | null
  promptOverride: string | null
}

export type TUpdateAgentsStatusRequest = {
  agentIds: string[]
  action: 'activate' | 'deactivate'
}

export type TUpdateAgentsStatusResponse = {
  successes: Array<{ agentId: string }>
  errors: Array<{ agentId: string; error: string }>
}

export type TAgentCentralizedContext = {
  selectedAgent: TAgent
  agents?: TAgent[] | null
  activeAgents: TAgent[]
  getAgentById: (agentId: string) => TAgent | null
  isError: boolean
  isSuccess: boolean
  isLoading: boolean
}

export enum EBulkEditAssistantsTabPanel {
  CoreBehavior = 'Core Behavior',
  Chatbot = 'Chatbot',
  Phone = 'Phone',
  Notifications = 'Notifications',
  AnalysisSettings = 'Analysis Settings',
}

export enum EBulkEditAppendReplaceMode {
  Append = 'append',
  Replace = 'replace',
}

export enum EManageAssistantsFiltersKeys {
  search = 'search',
}

export type TManageAssistantsFiltersForm = {
  [EManageAssistantsFiltersKeys.search]: string
}

export type TCloneAssistantForm = {
  [EAgentKeys.agentReferenceName]: string
  [EAgentKeys.businessName]: string
  [EAgentKeys.locationId]: string
}

export enum EManageAssistantsTabs {
  active = 'Active',
  inactive = 'Inactive',
  migration = 'Migration',
}

export type TManageAssistantsSelectionState = {
  selectedAgentIds: string[]
  onResetSelection: () => void
}

export enum EBulkEditCoreBehaviorSection {
  assistantPersonality = 'Assistant Personality',
  assistantName = 'Assistant Name',
  businessName = 'Business Name',
  calendarLink = 'Calendar Link',
  channelSpecificRules = 'Channel Specific Rules',
  coreQuestions = 'Core Questions',
  customCta = 'Custom CTA',
  customRules = 'Custom rules (override default rules)',
  mainCta = 'Main CTA',
  schedule = 'Schedule',
  service = 'Service',
  timezone = 'Timezone',
}

export enum EBulkEditChannelRuleType {
  chatRules = 'Chat Rules',
  emailRules = 'Email Rules',
  textRules = 'Text Rules',
  phoneRules = 'Phone Rules',
}

export enum EBulkEditPhoneSection {
  ambientAudio = 'Ambient Audio',
  assistantVoice = 'Assistant Voice',
  backupNumber = 'Backup number',
  closingMessageFollowUp = 'Closing Message Follow-up',
  customMessage = 'Call Continuity Custom Message',
  patience = 'Patience',
  phoneGreeting = 'Phone Greeting',
  phoneLanguage = 'Phone Language',
}

export enum EBulkEditChatbotSection {
  autoCloseAfterSeconds = 'Auto-close after (seconds)',
  autoCloseMessageAfterTime = 'Auto-close message after specified time',
  autoOpenAfterSeconds = 'Auto-open after (seconds)',
  autoOpenChatbot = 'Auto-open chatbot',
  chatbotAvatarImage = 'Chatbot Avatar Image',
  chatbotPopupIcon = 'Chatbot Popup Icon',
  chatbotGreeting = 'Chatbot Greeting',
  chatbotPopupMessage = 'Chatbot Popup Message',
  chatbotSubtitle = 'Chatbot Subtitle',
  chatbotTitle = 'Chatbot Title',
  showBranding = 'Show branding',
  showOnDevices = 'Show on devices',
  chatbotIconBackgroundColor = 'Chatbot Icon Background Color',
  chatbotIconColor = 'Chatbot Icon Color',
  chatbotIconShape = 'Chatbot Icon Shape',
  preChatForm = 'Pre chat form',
}

export enum EBulkEditNotificationsSection {
  notifyEmail = 'Notification Email',
  mobileConsent = 'Notification Consent',
  mobileNumber = 'Mobile Number',
  notificationType = 'Notification Type',
  textMethod = 'Text method',
}

export enum EBulkEditAnalysisSettingsSection {
  qualificationInstructions = 'Qualification instructions',
  summaryInstructions = 'Summary instructions',
  nextStepsInstructions = 'Next steps instructions',
}

export enum EBulkEditCustomInstructionsPath {
  qualificationInstructions = 'customInstructions[0].customQualifyInstructions',
  summaryInstructions = 'customInstructions[0].customSummaryInstructions',
  nextStepsInstructions = 'customInstructions[0].customNextstepsInstructions',
  rulesContext = 'customInstructions[0].rulesContext',
}

export enum EBulkEditChannelRulesPath {
  phoneRules = 'phoneSettings.phoneRules',
  chatRules = 'chatSettings.chatRules',
  textRules = 'textSettings.textRules',
  emailRules = 'emailSettings.emailRules',
}

export type TBulkEditFormChatSettings = {
  chatRules: string
  chatSubtitle: string
  chatTitle: string
  formId: string
  embedIconBGColor: string
  embedIconColor: string
  embedIconShape: string
  embedPopupAutoCloseDelaySeconds: number | ''
  embedPopupAutoCloseEnabled: boolean | null
  embedPopupCustomImageUrl: File | ''
  embedPopupDelaySeconds: number | ''
  embedPopupEnabled: boolean | null
  embedPopupMessage: string
  embedPopupDevices: string
}

export type TBulkEditFormEmailSettings = {
  emailRules: string
}

export type TBulkEditFormPhoneSettings = {
  backupNumber: string
  outageResponse: string
  patience: number
  phoneRules: string
  textFollowUpClosing: string
}

export type TBulkEditFormTextSettings = {
  textRules: string
}

export type TBulkEditFormCustomInstructionsItem = {
  [ECustomInstructionsKeys.customQualifyInstructions]: string
  [ECustomInstructionsKeys.customSummaryInstructions]: string
  [ECustomInstructionsKeys.customNextstepsInstructions]: string
  [ECustomInstructionsKeys.rulesContext]: string
}

export type TBulkEditAppendPayload = {
  agent?: {
    customInstructions?: Partial<TBulkEditFormCustomInstructionsItem>
  }
  phoneSettings?: { phoneRules?: string[] }
  chatSettings?: { chatRules?: string[] }
  emailSettings?: { emailRules?: string[] }
  textSettings?: { textRules?: string[] }
}

export type TUpdateBulkAgentsData = {
  payload: {
    agentIds: string[]
    payload: Partial<TUpdateAgent> & {
      append?: TBulkEditAppendPayload
    }
  }
}

export enum EBulkEditAssistantsFormKeys {
  agentLanguage = EAgentKeys.agentLanguage,
  assistantName = EAgentKeys.agentName,
  calendarLink = EAgentKeys.calendlyLink,
  businessName = EAgentKeys.businessName,
  chatIntro = EAgentKeys.chatIntro,
  chatbotImgURL = EAgentKeys.chatbotImgURL,
  chatSettings = EAgentKeys.chatSettings,
  cta = EAgentKeys.cta,
  customCta = EAgentKeys.ctaText,
  customInstructions = EAgentKeys.customInstructions,
  emailSettings = EAgentKeys.emailSettings,
  loopingAudio = EAgentKeys.loopingAudio,
  mobileConsent = EAgentKeys.mobileConsent,
  notifyEmail = EAgentKeys.notifyEmail,
  notifyMobile = EAgentKeys.notifyMobile,
  notifyType = EAgentKeys.notifyType,
  personality = EAgentKeys.personality,
  phoneIntro = EAgentKeys.phoneIntro,
  phoneSettings = EAgentKeys.phoneSettings,
  phoneVoice = EAgentKeys.phoneVoice,
  questions = EAgentKeys.questions,
  scheduleType = EAgentKeys.scheduleType,
  service = EAgentKeys.service,
  showBranding = EAgentKeys.showBranding,
  textMethod = EAgentKeys.textMethod,
  textSettings = EAgentKeys.textSettings,
  timezone = EAgentKeys.agentTimezone,
}

export type TBulkEditAssistantsForm = {
  [EBulkEditAssistantsFormKeys.agentLanguage]: string
  [EBulkEditAssistantsFormKeys.assistantName]: string
  [EBulkEditAssistantsFormKeys.businessName]: string
  [EBulkEditAssistantsFormKeys.calendarLink]: string
  [EBulkEditAssistantsFormKeys.chatIntro]: string
  [EBulkEditAssistantsFormKeys.chatbotImgURL]: File | ''
  [EBulkEditAssistantsFormKeys.chatSettings]: TBulkEditFormChatSettings
  [EBulkEditAssistantsFormKeys.cta]: ECTAFriendly | ''
  [EBulkEditAssistantsFormKeys.customCta]: string
  [EBulkEditAssistantsFormKeys.customInstructions]: TBulkEditFormCustomInstructionsItem[]
  [EBulkEditAssistantsFormKeys.emailSettings]: TBulkEditFormEmailSettings
  [EBulkEditAssistantsFormKeys.loopingAudio]: ELoopingAudio | ''
  [EBulkEditAssistantsFormKeys.mobileConsent]: boolean | null
  [EBulkEditAssistantsFormKeys.notifyEmail]: string
  [EBulkEditAssistantsFormKeys.notifyMobile]: string
  [EBulkEditAssistantsFormKeys.notifyType]: ENotificationType | ''
  [EBulkEditAssistantsFormKeys.personality]: string
  [EBulkEditAssistantsFormKeys.phoneIntro]: string
  [EBulkEditAssistantsFormKeys.phoneSettings]: TBulkEditFormPhoneSettings
  [EBulkEditAssistantsFormKeys.phoneVoice]: string
  [EBulkEditAssistantsFormKeys.questions]: string
  [EBulkEditAssistantsFormKeys.scheduleType]: EScheduleValues | ''
  [EBulkEditAssistantsFormKeys.service]: string
  [EBulkEditAssistantsFormKeys.showBranding]: boolean | null
  [EBulkEditAssistantsFormKeys.textMethod]: ETextMethodValues | ''
  [EBulkEditAssistantsFormKeys.textSettings]: TBulkEditFormTextSettings
  [EBulkEditAssistantsFormKeys.timezone]: string
}

export enum EAgentValueType {
  boolean = 'boolean',
  color = 'color',
  image = 'image',
  string = 'string',
}

/** Phone service GET /api/phone-numbers/orgs/:orgId */
export type TOrgPhoneLine = {
  phoneNumber: string
  agentId: string
  agentName: string
  status: string
  orchestrationType: string
  twilioSnapshot: unknown | null
  orchestratorDrift: unknown | null
  lastError?: string | null
}

export type TOrgPhoneNumbersSummary = {
  totalPhoneNumbers: number
  dbLegacyCount: number
  dbStandardCount: number
  dbRealtimeCount: number
  dbUnsetCount: number
  twilioMismatchCount: number | null
}

export type TOrgPhoneNumbersResponse = {
  orgId: string
  orgPhoneOrchestrationType: string
  phones: TOrgPhoneLine[]
  summary: TOrgPhoneNumbersSummary
}

export type TOrgMigrationRef = React.MutableRefObject<
  ((to: EOrchestratorType) => void) | null
>

export enum EPromptMetadataCategory {
  promptBank = 'prompt-bank',
}

export enum EPromptMetadataType {
  coreBehaviorPromptBank = 'core-behavior-prompt-bank',
  liveTransferPromptBank = 'live-transfer-prompt-bank',
  phoneSettingsPromptBank = 'phone-settings-prompt-bank',
}

export enum EPromptMetadataSubType {
  customRules = 'custom-rules',
  channelPhoneRules = 'channel-phone-rules',
  channelTextRules = 'channel-text-rules',
  channelChatRules = 'channel-chat-rules',
  channelEmailRules = 'channel-email-rules',
  ctaRules = 'cta-rules',
  coreQuestions = 'core-questions',
  phoneGreeting = 'phone-greeting',
  spokenPhrase = 'spoken-phrase',
  triggerPhrase = 'trigger-phrase',
}

export type TPromptMetadata = {
  id: string
  name: string
  category: EPromptMetadataCategory
  type: EPromptMetadataType
  subType: EPromptMetadataSubType | null
  description: string | null
  prompt: string
  active: boolean
  createdDate: string
  updatedDate: string
}

export type TPromptMetadataFilters = {
  id?: string
  name?: string
  category?: EPromptMetadataCategory
  type?: EPromptMetadataType
  subType?: EPromptMetadataSubType
  prompt?: string
  active?: boolean
}
